import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { APIPath } from '../utility/constants';
import { CommonUtility } from '../utility/common';
import { BaseService } from '../utility/services/base';
import { useAuth } from './auth';
import { useGlobalContext } from './global';
import {
  sidebarMetadataSchema,
  type SidebarMetadata,
  type InventorySidebarMetadata,
} from '../schemas/sidebarMetadata';
import { formatNotificationCount } from '../utility/formatNotificationCount';

export { formatNotificationCount };

async function fetchSidebarMetadata(warehouseId: string): Promise<SidebarMetadata> {
  const query = CommonUtility.objectToParams({ warehouseId });
  const url = `${APIPath.organizationsSidebarMetadata}?${query}`;
  const raw = await BaseService.get<unknown>(url);
  const result = sidebarMetadataSchema.safeParse(raw);
  if (!result.success) {
    console.error('sidebar-metadata response validation failed', result.error);
    throw new Error('Invalid sidebar metadata response');
  }
  return result.data;
}

interface SidebarMetadataContextValue {
  inventory: InventorySidebarMetadata | null;
  isLoading: boolean;
  isError: boolean;
  refetch: UseQueryResult<SidebarMetadata, Error>['refetch'];
  formatNotificationCount: (count: number) => string;
}

const SidebarMetadataContext = createContext<SidebarMetadataContextValue | undefined>(
  undefined,
);

export function SidebarMetadataProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const { warehouseId } = useGlobalContext();

  const query = useQuery({
    queryKey: ['organizations', 'sidebar-metadata', warehouseId],
    queryFn: () => {
      if (!warehouseId) {
        throw new Error('sidebar-metadata requires warehouseId');
      }
      return fetchSidebarMetadata(warehouseId);
    },
    enabled: isAuthenticated === true && Boolean(warehouseId),
  });

  const value = useMemo<SidebarMetadataContextValue>(
    () => ({
      inventory: query.data?.inventory ?? null,
      isLoading: query.isLoading,
      isError: query.isError,
      refetch: query.refetch,
      formatNotificationCount,
    }),
    [query.data?.inventory, query.isLoading, query.isError, query.refetch],
  );

  return (
    <SidebarMetadataContext.Provider value={value}>
      {children}
    </SidebarMetadataContext.Provider>
  );
}

export function useSidebarMetadata(): SidebarMetadataContextValue {
  const ctx = useContext(SidebarMetadataContext);
  if (ctx === undefined) {
    throw new Error('useSidebarMetadata must be used within a SidebarMetadataProvider');
  }
  return ctx;
}
